
import { useTranslation } from 'react-i18next';

interface TestimonialCardProps {
  name: string;
  location: string;
  rating: number;
  text: string;
}

function TestimonialCard({ name, location, rating, text }: TestimonialCardProps) {
  return (
    <div className="bg-white-cream/90 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-wood-warm/20 flex-shrink-0 w-[320px] lg:w-[380px]">
      <div className="flex items-center gap-1 mb-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <span key={i} className={i < rating ? 'text-yellow-400 text-lg' : 'text-gray-300'}>★</span>
        ))}
      </div>
      <p className="text-gray-700 mb-4 italic">"{text}"</p>
      <div className="flex items-center justify-between">
        <span className="font-semibold">{name}</span>
        <span className="text-gray-500 text-sm">{location}</span>
      </div>
    </div>
  );
}

interface StatCardProps {
  number: string;
  label: string;
}

function StatCard({ number, label }: StatCardProps) {
  return (
    <div>
      <div className="text-3xl lg:text-4xl font-bold text-green-dark mb-2">{number}</div>
      <div className="text-gray-600">{label}</div>
    </div>
  );
}

export function TestimonialsSection() {
  const { t } = useTranslation();

  const testimonials = [
    { 
      name: 'Aino M.', 
      location: 'Helsinki', 
      rating: 5, 
      category: 'coupleAnniversaryGift',
      text: t('testimonials.reviews.coupleAnniversaryGift.aino')
    },
    { 
      name: 'Eero K.', 
      location: 'Tampere', 
      rating: 5, 
      category: 'birthCity',
      text: t('testimonials.reviews.birthCity.eero')
    },
    { 
      name: 'Liisa V.', 
      location: 'Turku', 
      rating: 5, 
      category: 'myHome',
      text: t('testimonials.reviews.myHome.liisa')
    },
    { 
      name: 'Jukka L.', 
      location: 'Oulu', 
      rating: 5, 
      category: 'coupleAnniversaryGift',
      text: t('testimonials.reviews.coupleAnniversaryGift.jukka')
    },
    { 
      name: 'Sanna H.', 
      location: 'Jyväskylä', 
      rating: 5, 
      category: 'birthCity',
      text: t('testimonials.reviews.birthCity.sanna')
    },
    { 
      name: 'Matti R.', 
      location: 'Lahti', 
      rating: 5, 
      category: 'myHome',
      text: t('testimonials.reviews.myHome.matti')
    },
  ];

  const stats = [
    { number: '4.9★', label: t('testimonials.stats.averageRating') },
    { number: t('testimonials.stats.averageDeliveryDays'), label: t('testimonials.stats.averageDelivery') },
  ];

  return (
    <section className="bg-gradient-to-b from-white/80 to-white-cream/60 py-16 lg:py-24">
      <div className="container mx-auto px-4 max-w-7xl">
        <h2 className="text-3xl lg:text-4xl font-bold text-center mb-4">
          {t('testimonials.title')}
        </h2>
        <p className="text-center text-gray-600 mb-12">
          {t('testimonials.subtitle')}
        </p>

        {/* Horizontal carousel */}
        <div className="mb-16">
          <div className="overflow-x-auto pb-4 custom-scrollbar -mx-4 px-4 md:mx-0 md:px-0">
            <div className="flex gap-6">
              {testimonials.map((testimonial, idx) => (
                <TestimonialCard
                  key={idx}
                  name={testimonial.name}
                  location={testimonial.location}
                  rating={testimonial.rating}
                  text={testimonial.text}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-2 gap-8 text-center">
          {stats.map((stat, idx) => (
            <StatCard key={idx} number={stat.number} label={stat.label} />
          ))}
        </div>
      </div>
    </section>
  );
}

